import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { getDisplayName } from '../utils/formatters';
import { WORK_ORDER_STATUS, INVOICE_STATUS } from '../constants/statusEnums';
import { fmtDate, companyDateKey, companyWallClockToUtc, addDaysToDateKey } from '../utils/dateFormatters';
import { isReceivable, invoiceBalance } from '../utils/invoiceAr';
import { currentTerm } from '../utils/brandingUtils';
import { getSchedulingSettings, DAY_NAMES } from '../utils/smartScheduling';
import { fetchAdminDashboardDataLegacy, fetchRisks, fetchRecentActivity } from './adminDashboardLegacyFetch';

// Helper functions (kept from original)
const calcDelta = (current, previous) => {
  if (!previous || previous === 0 || current === undefined || current === null) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const getPrevPeriod = (preset, start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  
  switch (preset) {
    case 'TODAY':
      s.setDate(s.getDate() - 1);
      e.setDate(e.getDate() - 1);
      break;
    case 'WEEK':
      s.setDate(s.getDate() - 7);
      e.setDate(e.getDate() - 7);
      break;
    case 'MONTH':
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
      break;
    default:
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
  }
  
  return { start: s, end: e };
};

const countWeekdays = (s, e) => {
  let d = new Date(s), cnt = 0;
  d.setHours(0,0,0,0);
  const endD = new Date(e); endD.setHours(0,0,0,0);
  while (d <= endD) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) cnt++;
    d.setDate(d.getDate() + 1);
  }
  return cnt;
};


// ============== ONE READ ==============
//
// Page speed phase 1 (docs/plans/2026-10-25-page-speed.md). Every figure on the Business
// Dashboard comes from ONE database call, get_admin_dashboard_summary
// (supabase/migrations/20261025_admin_dashboard_summary.sql). It replaced ~50 requests in three
// waves (the main fetches, then the leaderboard/heatmap lookups, then Risks fetched twice because
// its query key carried utilization). Each figure keeps the definition it had here; the SQL names
// the function each block replaced, and the old and new values were compared per tile on live
// data for five companies before the switch. What stays in this file is presentation and the
// rules owned elsewhere: rounding of rates, tile labels and colours, isReceivable
// (utils/invoiceAr), business hours (getSchedulingSettings) and the weekday count.
//
// The function runs as the caller (SECURITY INVOKER), so RLS decides what is counted, exactly
// as it did for the separate queries.

const buildRevenueSeries = (startISO, endISO, cashByDay) => {
  // One bucket per COMPANY calendar day. These were UTC days, so every payment taken after
  // 5pm Pacific landed in tomorrow's bar.
  const days = [];
  const endKey = companyDateKey(endISO);
  for (let k = companyDateKey(startISO); k && endKey && k <= endKey; k = addDaysToDateKey(k, 1)) {
    days.push(k);
  }
  return days.map((d) => Number(cashByDay?.[d]) || 0);
};

// Tech Scorecards: work finished or cancelled in the period, per assigned tech (audit
// 2026-10-17; fix list 2026-10-24 #7). Names come from the company's employees by employee id or
// user id (#4834: a name lookup, not a scheduling gate).
const buildTechScoreCards = (techStats, employees, start, end) => {
  const techScoreCards = [];
  if (!techStats || techStats.length === 0) return techScoreCards;
  const weekdays = countWeekdays(start, end);
  const nameByEmpId = new Map();
  (employees || []).forEach((emp) => {
    const name = getDisplayName(emp);
    nameByEmpId.set(emp.id, name);
    if (emp.user_id) nameByEmpId.set(emp.user_id, name);
  });
  techStats.forEach((stats) => {
    const techId = stats.id;
    const name = nameByEmpId.get(techId) || 'Unknown Tech';
    const completed = Number(stats.completed) || 0;
    const cancelled = Number(stats.cancelled) || 0;
    const revenue = Number(stats.revenue) || 0;
    const avgTicket = completed > 0 ? revenue / completed : 0;
    const completionDenom = completed + cancelled;
    // null renders "—": nothing finished or cancelled is not a 0% completion rate.
    const completionRate = completionDenom > 0 ? Math.round((completed / completionDenom) * 100) : null;
    // #4833: one decimal rounded 1 job / 22 weekdays to "0.0" — a tech with real work read as idle.
    const jobsPerDay = weekdays > 0 ? (completed / weekdays).toFixed(2) : '0.00';
    techScoreCards.push({ id: techId, name, completed, revenue, avgTicket, completionRate, jobsPerDay });
  });
  return techScoreCards;
};

// Leaderboard: finished work per creator, top 5.
const buildLeaderboard = (rows) => (rows || []).map((r) => ({
  techId: r.tech_id,
  count: Number(r.count) || 0,
  name: r.found
    ? (r.name || [r.first_name, r.last_name].filter(Boolean).join(' ') || currentTerm('worker'))
    : r.tech_id,
}));

// Utilization capacity (#2267): SCHEDULABLE employees x the company's configured hours for each
// day in the range, on the same basis as the Calendar's Utilization tile.
const computeCapacityHours = (hours, start, end, schedulable) => {
  const hoursForDay = (d) => {
    const day = hours?.[DAY_NAMES[d.getDay()]];
    if (!day?.open || !day.start || !day.end) return 0;
    const [sh, sm] = day.start.split(':').map(Number);
    const [eh, em] = day.end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
  };
  let hoursPerEmployee = 0;
  const cursor = new Date(start); cursor.setHours(0, 0, 0, 0);
  const lastDay = new Date(end); lastDay.setHours(0, 0, 0, 0);
  while (cursor <= lastDay) {
    hoursPerEmployee += hoursForDay(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  return (schedulable || 0) * hoursPerEmployee;
};

// Capacity heatmap: the next 7 company calendar days (audit 2026-10-17).
const buildCapacityHeatmap = (heatByDay, availableTechs) => {
  const addDays = (ymd, n) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
  };
  const todayKey = companyDateKey(new Date());
  const capacityHeatmap = [];
  for (let i = 0; i < 7; i++) {
    const dayStr = addDays(todayKey, i);
    const dayDate = new Date(companyWallClockToUtc(dayStr, '12:00'));
    const dayName = fmtDate(dayDate, { weekday: 'short' });
    const jobCount = Number(heatByDay?.[dayStr]?.jobs) || 0;
    const totalHours = Number(heatByDay?.[dayStr]?.hours) || 0;
    const techCapacity = availableTechs * 8;
    const fillRate = techCapacity > 0 ? Math.round((totalHours / techCapacity) * 100) : 0;
    // An empty day (nothing scheduled) reads neutral — not a warning. Only color
    // by utilization when there's actual work booked. Includes dark-theme variants.
    let colorClass = 'bg-gray-100 text-gray-700 dark:bg-slate-700/60 dark:text-gray-300';
    if (jobCount > 0) {
      if (fillRate < 40) colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
      else if (fillRate > 90) colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
      else colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    }
    capacityHeatmap.push({
      date: dayStr,
      dayName,
      jobCount,
      totalHours: Math.round(totalHours * 10) / 10,
      fillRate,
      colorClass
    });
  }
  return capacityHeatmap;
};

// Recent activity: the newest work order, quote, payment and invoice events, merged.
const buildRecentActivity = async (s) => {
  const activities = [];
  const { BanknotesIcon, DocumentTextIcon } = await import('@heroicons/react/24/outline');

  (s.recent_work_orders || []).forEach((wo) => {
    let message = '';
    if (wo.status === WORK_ORDER_STATUS.SCHEDULED) message = `Work order scheduled — ${wo.title}`;
    else if (wo.status === WORK_ORDER_STATUS.COMPLETED) message = `Work order completed — ${wo.title}`;
    if (message) {
      activities.push({ id: `wo-${wo.id}`, workOrderId: wo.id, type: 'work_order', message, icon: DocumentTextIcon, ts: wo.updated_at, time: fmtDate(wo.updated_at) });
    }
  });

  (s.recent_quotes || []).forEach((q) => {
    let message = '';
    if (q.status === WORK_ORDER_STATUS.SENT) message = `Quote sent — ${q.title}`;
    else if (q.status === WORK_ORDER_STATUS.CHANGES_REQUESTED) message = `Changes requested — ${q.title}`;
    if (message) {
      activities.push({ id: `quote-${q.id}`, workOrderId: q.id, type: 'quote', message, icon: DocumentTextIcon, ts: q.updated_at, time: fmtDate(q.updated_at) });
    }
  });

  (s.recent_payments || []).forEach((p) => {
    let message = '';
    if (p.status === 'completed') message = `Payment received — $${Number(p.amount).toLocaleString()}`;
    else if (p.status === 'failed') message = `Payment failed — $${Number(p.amount).toLocaleString()}`;
    if (message) {
      activities.push({ id: `payment-${p.id}`, type: 'payment', message, icon: BanknotesIcon, ts: p.updated_at, time: fmtDate(p.updated_at) });
    }
  });

  // Invoice events — an invoice being sent to (or viewed by) the customer is a
  // meaningful business activity that the work-order/payment feeds never surface.
  (s.recent_invoices || []).forEach((inv) => {
    const num = inv.invoice_number || 'Invoice';
    const when = inv.sent_at || inv.updated_at;
    let message = '';
    if (inv.status === INVOICE_STATUS.VIEWED) {
      message = `Invoice viewed — ${num}`;
    } else {
      const amt = Number(inv.total_amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      message = `Invoice sent — ${num} (${amt})`;
    }
    activities.push({ id: `invoice-${inv.id}`, type: 'invoice', message, icon: DocumentTextIcon, ts: when, time: fmtDate(when) });
  });

  activities.sort((a, b) => new Date(b.ts || b.time) - new Date(a.ts || a.time));
  return activities.slice(0, 10);
};

// Action Required.
const buildRisks = (s, utilizationPct, capacityHours) => {
  const newRisks = [];

  // 1) Overdue Invoices. A status in the A/R list and a past due date is NOT the definition of
  // overdue — it misses whether anything is still owed (INV-10006, 2026-09-19). isReceivable in
  // utils/invoiceAr is the single owner of that predicate.
  const overdueReceivables = (s.risk_overdue_invoices || []).filter(isReceivable);
  if (overdueReceivables.length > 0) {
    const totalAmount = overdueReceivables.reduce((sum, inv) => sum + invoiceBalance(inv), 0);
    newRisks.push({
      id: 'overdue-invoices',
      type: 'overdue_invoices',
      severity: 'high',
      title: 'Overdue Invoices',
      description: `${overdueReceivables.length} invoice${overdueReceivables.length > 1 ? 's' : ''} overdue — $${totalAmount.toLocaleString()} due`,
      count: overdueReceivables.length,
      action: { label: 'View Invoices', path: '/invoices?filter=overdue' }
    });
  }

  // 2) Aging Quotes
  const agingQuotes = Number(s.risk_aging_quotes) || 0;
  if (agingQuotes > 0) {
    newRisks.push({
      id: 'aging-quotes',
      type: 'aging_quotes',
      severity: 'medium',
      title: 'Aging Quotes',
      description: `${agingQuotes} quote${agingQuotes > 1 ? 's' : ''} waiting > 7 days — follow up needed`,
      count: agingQuotes,
      action: { label: 'View Quotes', path: '/quotes?filter=sent' }
    });
  }

  // 3) Unscheduled High-Value Jobs
  const unscheduledHighValue = Number(s.risk_unscheduled_high_value) || 0;
  if (unscheduledHighValue > 0) {
    newRisks.push({
      id: 'unscheduled-high-value',
      type: 'unscheduled_high_value',
      severity: 'high',
      title: 'Unscheduled High-Value Jobs',
      description: `${unscheduledHighValue} job${unscheduledHighValue > 1 ? 's' : ''} over $1K without a schedule`,
      count: unscheduledHighValue,
      action: { label: 'View Jobs', path: '/jobs?filter=unscheduled' }
    });
  }

  // 4) Jobs Starting Today with No Tech
  const todayNoTech = Number(s.risk_today_no_tech) || 0;
  if (todayNoTech > 0) {
    newRisks.push({
      id: 'today-no-tech',
      type: 'today_no_tech',
      severity: 'high',
      title: 'Jobs Starting Today — No Tech Assigned',
      description: `${todayNoTech} job${todayNoTech > 1 ? 's' : ''} scheduled today without a technician`,
      count: todayNoTech,
      action: { label: 'Dispatch Now', path: '/dispatch' }
    });
  }

  // 5) Overdue Jobs
  const overdueJobs = Number(s.risk_overdue_in_progress) || 0;
  if (overdueJobs > 0) {
    newRisks.push({
      id: 'overdue-jobs',
      type: 'overdue_jobs',
      severity: 'medium',
      title: 'Overdue Jobs',
      description: `${overdueJobs} job${overdueJobs > 1 ? 's' : ''} past scheduled end date still in progress`,
      count: overdueJobs,
      action: { label: 'View Jobs', path: '/jobs?filter=overdue' }
    });
  }

  // 6) Low Utilization Warning
  // #6247: the guard is "there is capacity to measure", not "utilization is above 0" -- a
  // rounded 0% never warned while 1% did.
  if (capacityHours > 0 && utilizationPct < 50) {
    newRisks.push({
      id: 'low-utilization',
      type: 'low_utilization',
      severity: 'low',
      title: 'Low Utilization Warning',
      description: `Team utilization at ${utilizationPct}% — consider scheduling more jobs or reducing capacity`,
      count: utilizationPct,
      action: { label: 'View Schedule', path: '/jobs' }
    });
  }

  return newRisks;
};

// ============== MAIN FETCH FUNCTION ==============
export const fetchAdminDashboardData = async ({ companyId, range }) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();
  const now = new Date();
  const presets = {
    TODAY: () => {
      const s = new Date(now); s.setHours(0,0,0,0);
      const e = new Date(now); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    WEEK: () => {
      const s = new Date(now); s.setHours(0,0,0,0); s.setDate(now.getDate() - now.getDay());
      const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    MONTH: () => {
      const s = new Date(now.getFullYear(), now.getMonth(), 1); s.setHours(0,0,0,0);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    LAST_30: () => {
      const s = new Date(now); s.setHours(0,0,0,0); s.setDate(now.getDate() - 30);
      const e = new Date(now); e.setHours(23,59,59,999);
      return { start: s, end: e };
    }
  };

  const { start, end } = range.start && range.end ? range : (presets[range.preset] || presets.LAST_30)();
  // The chosen days, bounded at the COMPANY's midnights (the revenue chart buckets by company day).
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const startISO = companyWallClockToUtc(dayKey(start), '00:00');
  const endISO = new Date(new Date(companyWallClockToUtc(addDaysToDateKey(dayKey(end), 1), '00:00')).getTime() - 1).toISOString();

  // "Today" as the Overdue Jobs count and the Action Required checks always took it: the
  // BROWSER's midnight, its end of day, and its midnight 7 days back.
  const localTodayStart = new Date(now); localTodayStart.setHours(0, 0, 0, 0);
  const localTodayEnd = new Date(now); localTodayEnd.setHours(23, 59, 59, 999);
  const localWeekAgo = new Date(localTodayStart); localWeekAgo.setDate(localTodayStart.getDate() - 7);

  // The summary and the business hours (capacity) do not depend on each other: run together.
  const [{ data: s, error }, { business_hours: hours }] = await Promise.all([
    supabase.rpc('get_admin_dashboard_summary', {
      p_company_id: companyId,
      p_start: startISO,
      p_end: endISO,
      p_now: now.toISOString(),
      p_local_today_start: localTodayStart.toISOString(),
      p_local_today_end: localTodayEnd.toISOString(),
      p_local_week_ago: localWeekAgo.toISOString(),
    }),
    // Dashboard only reads business_hours here; the cached settings row is fine for that.
    getSchedulingSettings(companyId, { fresh: false }),
  ]);
  if (error) {
    // The previous multi-query path, same shape. Two reasons it is still here:
    //  - deploy bridge: before the migration is applied the function does not exist (PGRST202);
    //  - behaviour freeze: the old page degraded per query (a failed read showed 0 and the rest
    //    of the dashboard rendered); without this one failed call would blank every tile.
    console.warn('get_admin_dashboard_summary failed, using the per-query path:', error.code || error.message);
    const legacy = await fetchAdminDashboardDataLegacy({ companyId, range });
    const [risks, recentActivity] = await Promise.all([
      fetchRisks(companyId, legacy.utilizationPct, legacy.capacityHours),
      fetchRecentActivity(companyId),
    ]);
    return { ...legacy, risks, recentActivity };
  }

  const n = (v) => Number(v) || 0;

  // Completion Rate: the Tech Scorecards' rule company-wide, finished / (finished + cancelled).
  const completionDenom = n(s.finished_count) + n(s.cancelled_count);
  const completionRate = completionDenom > 0 ? Math.round((n(s.finished_count) / completionDenom) * 100) : 0;

  const sentQuotes = n(s.funnel_sent);
  const acceptedQuotes = n(s.funnel_accepted);
  const quoteConversionRate = sentQuotes > 0 ? Math.round((acceptedQuotes / sentQuotes) * 100) : 0;

  const scheduledHours = n(s.scheduled_hours);
  const availableTechs = n(s.schedulable_employees);
  const capacityHours = computeCapacityHours(hours, start, end, availableTechs);
  const utilizationPct = capacityHours > 0 ? Math.round((scheduledHours / capacityHours) * 100) : 0;

  const onTimeSampleCount = n(s.on_time_sample);
  const onTimePct = onTimeSampleCount > 0 ? Math.round((n(s.on_time_count) / onTimeSampleCount) * 100) : 0;
  const backlogCount = n(s.backlog_count);
  const backlogAging = backlogCount > 0
    ? ((new Date() - n(s.backlog_avg_created_ms)) / (24 * 60 * 60 * 1000)).toFixed(1)
    : 0;
  // Cancellation Rate is a COHORT measure (board #1348): of the jobs created in the window,
  // the share now cancelled. A different question from the Cancelled money tile.
  const totalJobsCount = n(s.total_created);
  const cancellationRate = totalJobsCount > 0 ? Math.round((n(s.cancelled_created) / totalJobsCount) * 100) : 0;
  const durationSampleCount = n(s.duration_sample);
  const avgJobDuration = durationSampleCount > 0 ? (n(s.duration_hours) / durationSampleCount).toFixed(1) : 0;
  const sentQuotesCount = n(s.quotes_issued);
  const quoteToJobConversion = sentQuotesCount > 0 ? Math.round((n(s.quotes_accepted) / sentQuotesCount) * 100) : 0;

  // Unassigned list: the customer name, or 'Unknown' when the customer cannot be read.
  const unassignedRows = s.unassigned_jobs || [];
  const anyCustomer = unassignedRows.some((j) => j.customer_id);
  const unassignedJobs = unassignedRows.map(({ customer_name, ...j }) => (
    anyCustomer ? { ...j, customerName: customer_name || 'Unknown' } : j
  ));

  const recentActivity = await buildRecentActivity(s);

  return {
    activeEmployees: n(s.active_employees),
    // Two different questions that were sharing one field — and therefore one answer, shown
    // twice under two headings that promised different things.
    activeJobs: n(s.active_jobs),
    jobsThisWeek: n(s.jobs_in_range),
    monthlyRevenue: n(s.cash_revenue),
    // #5556: the calendar days the dashboard counted over, so a tile can open Reports on the
    // same window (local calendar day of the same start/end used for startISO/endISO).
    rangeStartDay: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
    rangeEndDay: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
    completionRate,
    revenueSeries: buildRevenueSeries(startISO, endISO, s.cash_by_day),
    bookedRevenue: n(s.booked_revenue),
    outstandingInvoiceAmount: n(s.outstanding_invoice_amount),
    uninvoicedCompleted: n(s.uninvoiced_completed),
    missedRevenue: n(s.missed_revenue),
    unscheduledJobs: n(s.unscheduled_jobs),
    outstandingInvoices: n(s.ar_outstanding),
    quoteConversionRate,
    sentQuotes,
    acceptedQuotes,
    overdueJobs: n(s.overdue_jobs),
    arAging: {
      bucket0_30: n(s.ar_bucket0_30),
      bucket31_60: n(s.ar_bucket31_60),
      bucket61_90: n(s.ar_bucket61_90),
      bucket90_plus: n(s.ar_bucket90_plus),
    },
    // #3911: the genuinely past-due receivable, for the A/R Aging card's Overdue line.
    overdueTotal: n(s.ar_overdue_total),
    overdueCount: n(s.ar_overdue_count),
    utilizationPct,
    scheduledHours,
    capacityHours,
    funnel: {
      sent: sentQuotes,
      accepted: acceptedQuotes,
      scheduled: n(s.funnel_scheduled),
      inProgress: n(s.funnel_in_progress),
      completed: n(s.funnel_completed),
    },
    leaderboard: buildLeaderboard(s.leaderboard),
    techScoreCards: buildTechScoreCards(s.tech_stats, s.employees, start, end),
    onTimePct,
    backlogAging,
    cancellationRate,
    noShowCount: n(s.no_show_count),
    avgJobDuration,
    quoteToJobConversion,
    onTimeSampleCount,
    backlogCount,
    totalJobsCount,
    durationSampleCount,
    sentQuotesCount,
    unassignedJobs,
    unassignedCount: n(s.unassigned_count),
    capacityHeatmap: buildCapacityHeatmap(s.heatmap, availableTechs),
    availableTechs,
    // Action Required and Recent Activity used to be two more queries (Risks ran twice: once
    // before utilization was known and again after). Both come from the same summary now.
    risks: buildRisks(s, utilizationPct, capacityHours),
    recentActivity,
    loading: false
  };
};

// ============== HOOK ==============
export const useAdminDashboardData = (companyId, range) => {
  return useQuery({
    queryKey: ['adminDashboard', companyId, range],
    queryFn: () => fetchAdminDashboardData({ companyId, range }),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });
};
